import type { RootState } from "@/store"
import { trocarView } from "../navegacao-kiosk"
import { atualizarKiosk } from "@/store/self-service-kiosk-slice"
import { useBalancaTotem } from "./use-balanca-totem"
import { balancaDisponivel } from "@/lib/balanca"
import { calcularItemPesado } from "./balanca-item"
import { uuidLocal } from "@/lib/id"
import { useDispatch, useSelector } from "react-redux"
import { useMemo, useRef, useState } from "react"
import { Alert, Box, CircularProgress, Skeleton, Snackbar, Stack, Typography } from "@mui/material"
import { useGetCatalogoQuery, useGetLojaAtualQuery, useAdicionarItensComandaMutation, useLazyGetContaTotemCheckoutQuery, type ProdutoCatalogo } from "@/api"
import DialogSelecaoBalanca, { type ProdutoPesavel } from "./dialog-selecao-balanca"
import ScaleIcon from '@mui/icons-material/Scale'

interface BalancaResumoProps {
    ativo?: boolean
}

function BalancaResumo({ ativo = true }: BalancaResumoProps): JSX.Element | null {
    const dispatch = useDispatch()
    const order = useSelector((state: RootState) => state.selfServiceKiosk.order)
    const identificadorBuscado = useSelector((state: RootState) => state.selfServiceKiosk.identificadorBuscado)
    const { data: loja } = useGetLojaAtualQuery()
    const disponivel = loja?.configuracao.totemBalancaAtiva === true && balancaDisponivel()
    const { data: catalogo, isSuccess: catalogoOk, isError: catalogoFalhou } = useGetCatalogoQuery('CARDAPIO_TOTEM_CHECKOUT', { skip: !disponivel })

    const produtosPesaveis = useMemo<ProdutoPesavel[]>(() => {
        const vistos = new Set<number>()
        const lista: ProdutoPesavel[] = []

        for (const grupo of catalogo?.grupos ?? []) {
            for (const produto of grupo.produtos) {
                if (!produto.pesavel || vistos.has(produto.id)) continue

                vistos.add(produto.id)
                lista.push({ produto, cor: grupo.cor })
            }
        }

        return lista
    }, [catalogo])

    const habilitado = ativo && disponivel && produtosPesaveis.length > 0
    const balanca = useBalancaTotem(habilitado)
    const [adicionarItens, { isLoading: enviando }] = useAdicionarItensComandaMutation()
    const [buscarConta] = useLazyGetContaTotemCheckoutQuery()
    const [erro, setErro] = useState<string | null>(null)
    const emAndamento = useRef(false)
    const chaveEnvio = useRef<string | null>(null)

    const handleSelecionar = async (produto: ProdutoCatalogo): Promise<void> => {
        if (balanca.pesoKg == null || emAndamento.current) return
        emAndamento.current = true

        chaveEnvio.current ??= uuidLocal()

        const item = calcularItemPesado(produto, balanca.pesoKg)
        try {
            await adicionarItens({
                identificador: identificadorBuscado,
                itens: [item],
                idPedidoIntegracao: order?.idPedidoIntegracao ?? undefined,
                chaveIdempotencia: chaveEnvio.current,
            }).unwrap()

            const conta = await buscarConta({ identificador: identificadorBuscado }).unwrap()
            const pedidos = conta.pedidos ?? []

            if (pedidos.length > 1) {
                trocarView(dispatch, { currentView: 'VIEW_MULTIPLE_ORDERS', multipleOrders: pedidos, hasMultipleOrders: true })
            } else {
                dispatch(atualizarKiosk({ order: pedidos[0] ?? null, hasMultipleOrders: false }))
            }

            chaveEnvio.current = null
            balanca.confirmarAdicao()
        } catch (err) {
            console.error('Erro ao adicionar item pesado à comanda:', err)
            const mensagem = (err as { data?: { mensagem?: string } })?.data?.mensagem
            setErro(mensagem ?? 'Não foi possível adicionar o item pesado. Tente novamente.')
        } finally {
            emAndamento.current = false
        }
    }

    if (!disponivel || !ativo) return null

    if (!catalogoOk && !catalogoFalhou) {
        return (
            <Box sx={{ mt: 2, borderRadius: 3 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center">
                    <ScaleIcon color="disabled" />

                    <Skeleton variant="text" width={280} sx={{ fontSize: '0.875rem' }} />
                </Stack>
            </Box>
        )
    }

    if (!habilitado) return null

    return (
        <>
            <Box
                sx={{
                    mt: 2,
                    borderRadius: 3,
                }}
            >
                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center">
                    {balanca.status === 'DETECTADO' ? (
                        <CircularProgress size={24} />
                    ) : (
                        <ScaleIcon color={balanca.mensagemErro ? 'warning' : 'primary'} />
                    )}

                    <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                        {mensagemStatus(balanca.status, balanca.mensagemErro)}
                    </Typography>
                </Stack>
            </Box>

            <DialogSelecaoBalanca
                open={balanca.status === 'DETECTADO'}
                pesoKg={balanca.pesoKg}
                produtos={produtosPesaveis}
                enviando={enviando}
                onSelecionar={handleSelecionar}
                onClose={() => {
                    chaveEnvio.current = null
                    balanca.cancelar()
                }}
            />

            <Snackbar
                open={!!erro}
                autoHideDuration={5000}
                onClose={() => setErro(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity="error" variant="filled" onClose={() => setErro(null)} sx={{ fontSize: '1.1rem' }}>
                    {erro}
                </Alert>
            </Snackbar>
        </>
    )
}

function mensagemStatus(status: ReturnType<typeof useBalancaTotem>['status'], erro: string | null): string {
    if (erro) return erro
    switch (status) {
        case 'RETIRAR':
            return 'Retire o item da balança para pesar o próximo.'
        case 'DETECTADO':
            return 'Peso detectado — selecione o item.'
        case 'AGUARDANDO':
        default:
            return 'Tem algo para pesar? Coloque o item na balança.'
    }
}

export default BalancaResumo
